/** @jsxImportSource theme-ui */
import { useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  ButtonGroup,
  SearchInput,
  SelectButton,
  Avatar,
  Text,
  Button,
  ButtonGroupOnValueChangeParam,
  SelectOnSelectParam,
  useToastContext,
  useBreakpointIndex,
} from '@infotrack/zenith-ui';
import { useNavigate } from 'react-router-dom';
import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';
import {
  headerContainerStyles,
  headerTopRowStyles,
  periodToggleRowStyles,
  searchFilterRowStyles,
  resultsRowStyles,
  leftSectionStyles,
  desktopLogoStyles,
  filtersSectionStyles,
  searchInputWrapperStyles,
  selectButtonWrapperStyles,
  resultsTextStyles,
  rightSectionStyles,
} from './topFiveHeader.styles';
import {
  PeriodType,
  PERIOD_OPTIONS,
  FilterType,
  FILTER_TYPE_OPTIONS,
} from '../constants/topFiveOptions';
import { QUERY_CONFIG } from '@/api/queryConstants';

type TopFiveHeaderProps = {
  /**
   * @default 0
   */
  resultCount?: number;
  onPeriodChange?: (period: PeriodType) => void;
  /**
   * @default "current"
   */
  selectedPeriod?: PeriodType;
  onSearch?: (searchText: string, searchType: FilterType) => void;
  onClear?: () => void;
  /**
   * @default ""
   */
  searchText?: string;
  /**
   * @default "0"
   */
  searchType?: FilterType;
  onSearchTypeChange?: (type: FilterType) => void;
};

export const TopFiveHeader = ({
  resultCount = 0,
  onPeriodChange,
  selectedPeriod = 'current',
  onSearch,
  onClear,
  searchText = '',
  searchType = '0',
  onSearchTypeChange,
}: TopFiveHeaderProps) => {
  const navigate = useNavigate();
  const toastContext = useToastContext();
  const breakpointIndex = useBreakpointIndex();
  const isDesktop = breakpointIndex >= 2; // desktopSmall and above

  // Auth data for user avatar
  const { data: authResponse } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      enabled: true,
      staleTime: QUERY_CONFIG.STALE_TIME.MEDIUM,
    },
  });

  const apiUser = authResponse?.data;

  // Check if user has permission to add TopFive
  const hasAddPermission = useMemo(() => {
    return apiUser?.permissions?.hasAddTopFiveAccess ?? false;
  }, [apiUser?.permissions?.hasAddTopFiveAccess]);

  // Memoize display name calculation
  const displayName = useMemo(() => {
    if (!apiUser) return 'User';

    if (apiUser.firstName && apiUser.lastName) {
      return `${apiUser.firstName} ${apiUser.lastName}`;
    }

    return (
      apiUser.preferredName || apiUser.firstName || apiUser.lastName || 'User'
    );
  }, [apiUser]);

  // Show validation message for search
  const showSearchValidationMessage = useCallback(() => {
    toastContext.error({
      title: 'Search Validation',
      message: 'Search requires at least 3 characters.',
      duration: 5,
    });
  }, [toastContext]);

  // Handle period change
  const handlePeriodChange = useCallback(
    (param: ButtonGroupOnValueChangeParam) => {
      onPeriodChange?.(param.selectedOption.value as PeriodType);
    },
    [onPeriodChange]
  );

  // Handle search type selection
  const handleSearchTypeSelect = useCallback(
    (params: SelectOnSelectParam) => {
      if (params.selectedOption) {
        const newSearchType = params.selectedOption.value as FilterType;
        onSearchTypeChange?.(newSearchType);
      }
    },
    [onSearchTypeChange]
  );

  // Handle search execution (Enter key)
  const handleSearchExecution = useCallback(
    (searchValue: string) => {
      if (searchValue.trim() && searchValue.trim().length >= 3) {
        onSearch?.(searchValue.trim(), searchType);
      } else if (searchValue.trim() && searchValue.trim().length < 3) {
        showSearchValidationMessage();
      }
    },
    [searchType, onSearch, showSearchValidationMessage]
  );

  // Handle search input clear (only when user explicitly clears)
  const handleSearchClear = useCallback(() => {
    onClear?.();
  }, [onClear]);

  // Transform filter options for SelectButton
  const selectButtonOptions = useMemo(
    () =>
      FILTER_TYPE_OPTIONS.map(option => ({
        value: option.value,
        text: option.text,
      })),
    []
  );

  // Reusable component definitions
  const logo = (
    <Text variant="hero" color="primary/600">
      iTop5
    </Text>
  );

  const avatar = <Avatar name={displayName} />;

  const periodToggle = (
    <ButtonGroup
      options={PERIOD_OPTIONS}
      value={selectedPeriod}
      onValueChange={handlePeriodChange}
      aria-label="Select period"
      size="small"
    />
  );

  const searchControls = (
    <>
      <Box sx={searchInputWrapperStyles}>
        <SearchInput
          placeholder="Search..."
          value={searchText}
          onSearch={handleSearchExecution}
          onClear={handleSearchClear}
          debounce={0}
          aria-label="Search input"
          size="small"
        />
      </Box>
      <Box sx={selectButtonWrapperStyles}>
        <SelectButton
          options={selectButtonOptions}
          value={searchType}
          onSelect={handleSearchTypeSelect}
          placeholder="Search Type"
          aria-label="Select search type"
          size="small"
        />
      </Box>
    </>
  );

  const resultsText = (
    <Text variant="caption">
      {isDesktop ? 'Displaying ' : ''}
      {resultCount} people
    </Text>
  );

  // Desktop layout
  if (isDesktop) {
    return (
      <Flex role="banner" sx={headerContainerStyles}>
        <Flex sx={leftSectionStyles}>
          <Box sx={desktopLogoStyles}>{logo}</Box>
          <Flex
            sx={filtersSectionStyles}
            role="toolbar"
            aria-label="Top Five filters"
          >
            {periodToggle}
            {searchControls}
          </Flex>
          <Box role="status" aria-live="polite" sx={resultsTextStyles}>
            {resultsText}
          </Box>
        </Flex>
        <Flex sx={rightSectionStyles}>
          {hasAddPermission && (
            <>
              <Button
                variant="primary"
                size="small"
                onClick={() => navigate('/topfive/add')}
                aria-label="Add or Update TopFive for Staff"
              >
                Add TopFive
              </Button>
              <Box sx={{ width: '0.5rem' }} />
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate('/import')}
                aria-label="Import TopFives from CSV"
              >
                Import
              </Button>
              <Box sx={{ width: '1rem' }} />
            </>
          )}
          {avatar}
        </Flex>
      </Flex>
    );
  }

  // Mobile layout
  return (
    <Flex role="banner" sx={headerContainerStyles}>
      <Flex sx={headerTopRowStyles}>
        {logo}
        <Flex sx={{ gap: 1, alignItems: 'center' }}>
          {hasAddPermission && (
            <>
              <Button
                variant="primary"
                size="small"
                onClick={() => navigate('/topfive/add')}
                aria-label="Add or Update TopFive for Staff"
              >
                Add
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate('/import')}
                aria-label="Import TopFives from CSV"
              >
                Import
              </Button>
            </>
          )}
          {avatar}
        </Flex>
      </Flex>
      <Flex sx={periodToggleRowStyles}>{periodToggle}</Flex>
      <Flex sx={searchFilterRowStyles}>{searchControls}</Flex>
      <Flex sx={resultsRowStyles} role="status" aria-live="polite">
        <Box sx={resultsTextStyles}>{resultsText}</Box>
      </Flex>
    </Flex>
  );
};
